import {
	Button,
	ControlledCheckbox,
	ControlledInput,
	ControlledSelect,
} from "@dune/ui";
import { contactCreationRoles, roleToAccountTypeText, roles } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import { forgetPassword } from "../../api/mutations/user.mutations";
import { contactsQueryKeys } from "../../api/query-keys/contacts.query-keys";
import { BrokerContactForm } from "./BrokerContactForm";
import { DuneContactForm } from "./DuneContactForm";
import { InsurerContactForm } from "./InsurerContactForm";
import { SyndicContactForm } from "./SyndicContactForm";
import { TPAContactForm } from "./TPAContactForm";
import { ContactFormData } from "./useAddContactForm";

export type AddOrUpdateContactFormModeProps = {
	mode: "add" | "update";
};

export const ContactForm = ({ mode }: AddOrUpdateContactFormModeProps) => {
	const formMethods = useFormContext<ContactFormData>();
	const {
		control,
		watch,
		formState: { errors },
	} = formMethods;

	const email = watch("email");

	const queryClient = useQueryClient();

	const { mutate: forgetPasswordMutate, isPending: isForgetPasswordPending } =
		useMutation({
			mutationFn: forgetPassword,
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: contactsQueryKeys.lists(),
				});
			},
		});

	const typeSelected = watch("type");
	const giveAccessToPlatform = watch("giveAccessToPlatform");

	const rolesToUse = mode === "update" ? roles : contactCreationRoles;
	const rolesSelectOptions = rolesToUse.map((role) => ({
		label: roleToAccountTypeText[role],
		value: role,
	}));

	return (
		<>
			<ControlledSelect
				control={control}
				name="type"
				label="Type"
				options={rolesSelectOptions}
				error={errors.type?.message?.toString()}
				required
				disabled={mode === "update"}
			/>
			{typeSelected === "BROKER" && <BrokerContactForm mode={mode} />}
			{typeSelected === "SYNDIC" && <SyndicContactForm mode={mode} />}
			{typeSelected === "DUNE" && <DuneContactForm />}
			{typeSelected === "INSURER" && <InsurerContactForm mode={mode} />}
			{typeSelected === "TPA" && <TPAContactForm mode={mode} />}
			<div className="flex gap-4">
				<ControlledInput
					name="lastname"
					label="Nom"
					control={control}
					error={errors.lastname?.message?.toString()}
					required
				/>
				<ControlledInput
					name="firstname"
					label="Prénom"
					control={control}
					error={errors.firstname?.message?.toString()}
					required
				/>
			</div>
			<ControlledInput
				name="email"
				label="Email"
				control={control}
				error={errors.email?.message?.toString()}
				required={!!giveAccessToPlatform}
			/>
			{mode === "add" && (
				<ControlledCheckbox
					control={control}
					name="giveAccessToPlatform"
					label="Ajouter en tant qu'utilisateur"
					error={errors.giveAccessToPlatform?.message?.toString()}
				/>
			)}
			{mode === "update" && giveAccessToPlatform && (
				<Button
					onClick={() => {
						forgetPasswordMutate(email);
					}}
					isLoading={isForgetPasswordPending}
				>
					Renvoyer une invitation
				</Button>
			)}
		</>
	);
};
